import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, MessageSquare, Calendar, Camera, Mail, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userType, setUserType] = useState(null); // 'admin' or 'member'
  const [notificationError, setNotificationError] = useState("");
  const [retryCycle, setRetryCycle] = useState(0);
  const dropdownRef = useRef(null);
  const fetchInFlight = useRef(false);

  // Detect user type
  useEffect(() => {
    const adminToken = localStorage.getItem("gio_token");
    const memberToken = localStorage.getItem("memberToken");
    
    if (adminToken) {
      setUserType("admin");
    } else if (memberToken) {
      setUserType("member");
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!userType || fetchInFlight.current) return false;
    fetchInFlight.current = true;
    try {

      // Admin sees: Messages, Bookings, Model Applications
      if (userType === "admin") {
        const results = await Promise.allSettled([
          api.get("/api/messages/inbox", { params: { limit: 50 } }),
          api.get("/api/escort-bookings", { params: { limit: 50 } }),
          api.get("/api/model-applications/", { params: { limit: 50 } }),
        ]);
        const failures = results.filter((result) => result.status === "rejected").length;
        setNotificationError(failures ? "Some notifications are unavailable." : "");
        const messages = results[0].status === "fulfilled" ? results[0].value.data?.messages || [] : [];
        const bookings = results[1].status === "fulfilled" ? results[1].value.data || [] : [];
        const modelApps = results[2].status === "fulfilled" ? results[2].value.data || [] : [];

        const allNotifications = [];

        // Add unread messages
        const unreadMessages = messages
          .filter(m => !m.read)
          .slice(0, 5)
          .map(m => ({
            id: `msg-${m.id}`,
            type: "message",
            title: "New Message",
            text: m.name ? `${m.name}: ${m.message?.substring(0, 60)}...` : m.message?.substring(0, 60) + "...",
            time: new Date(m.created_at),
            link: "/crm/messages",
            icon: MessageSquare,
            color: "purple",
          }));

        // Add pending bookings
        const pendingBookings = bookings
          .filter(b => b.status === "pending")
          .slice(0, 5)
          .map(b => ({
            id: `booking-${b.id}`,
            type: "booking",
            title: "Booking Request",
            text: `${b.name} - ${b.service_type} (${b.duration})`,
            time: new Date(b.created_at),
            link: "/crm/bookings",
            icon: Calendar,
            color: "pink",
          }));

        // Add pending model applications
        const pendingApps = modelApps
          .filter(a => a.status === "pending")
          .slice(0, 5)
          .map(a => ({
            id: `app-${a.id}`,
            type: "application",
            title: "Model Application",
            text: `${a.name} - ${a.experience || "New application"}`,
            time: new Date(a.created_at),
            link: "/crm/model-applications",
            icon: Camera,
            color: "blue",
          }));

        allNotifications.push(...unreadMessages, ...pendingBookings, ...pendingApps);
        
        // Sort by time (newest first)
        allNotifications.sort((a, b) => b.time - a.time);

        setNotifications(allNotifications.slice(0, 10));
        setUnreadCount(allNotifications.length);
        return failures === 0;
      } 
      // Members see: Their own notifications (order confirmations, membership updates, content releases)
      else if (userType === "member") {
        const response = await api.get("/api/notifications/member");
        const memberNotifs = response.data || [];
        setNotificationError("");
        
        const allNotifications = memberNotifs.map(n => ({
          id: n.id,
          type: n.type || "info",
          title: n.title,
          text: n.message,
          time: new Date(n.created_at),
          link: n.link || "/members/profile",
          icon: n.type === "order" ? Mail : n.type === "content" ? Camera : Bell,
          color: "purple",
        }));

        allNotifications.sort((a, b) => b.time - a.time);
        setNotifications(allNotifications.slice(0, 10));
        setUnreadCount(allNotifications.filter(n => !n.read).length);
        return true;
      }
      return true;
    } catch (err) {
      setNotificationError("Notifications are unavailable.");
      console.error("Failed to fetch notifications:", err);
      return false;
    } finally {
      fetchInFlight.current = false;
    }
  }, [userType]);

  useEffect(() => {
    if (!userType) return undefined;
    let active = true;
    let timer;
    let failures = 0;
    const refresh = async () => {
      const available = await fetchNotifications();
      if (!active) return;
      failures = available ? 0 : failures + 1;
      if (failures >= 4) return;
      timer = window.setTimeout(refresh, available ? 60000 : Math.min(60000, 1000 * (2 ** (failures - 1))));
    };
    refresh();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [userType, fetchNotifications, retryCycle]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getColorClasses = (color) => {
    const colors = {
      purple: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30" },
      pink: { bg: "bg-pink-500/10", text: "text-pink-400", border: "border-pink-500/30" },
      blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
    };
    return colors[color] || colors.purple;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-white/5 transition"
        aria-label="Notifications"
      >
        <Bell size={20} className="text-zinc-400" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-96 rounded-2xl border border-white/10 backdrop-blur-xl shadow-2xl overflow-hidden z-50"
          style={{ background: "rgba(0, 0, 0, 0.95)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <h3 className="text-lg font-bold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <p className="text-xs text-zinc-500">{unreadCount} unread</p>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg hover:bg-white/5 transition"
            >
              <X size={18} className="text-zinc-400" />
            </button>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notificationError && (
              <div role="status" className="border-b border-amber-900/60 p-3 text-sm text-amber-300">
                {notificationError} <button type="button" className="ml-2 underline" onClick={() => setRetryCycle((value) => value + 1)}>Retry</button>
              </div>
            )}
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={48} className="mx-auto mb-3 text-zinc-600" />
                <p className="text-sm text-zinc-500">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {notifications.map((notif) => {
                  const colors = getColorClasses(notif.color);
                  const IconComponent = notif.icon;
                  
                  return (
                    <a
                      key={notif.id}
                      href={notif.link}
                      className="block p-4 hover:bg-white/5 transition"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg} border ${colors.border}`}>
                          <IconComponent size={18} className={colors.text} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-sm font-semibold text-white">
                              {notif.title}
                            </span>
                            <span className="text-xs text-zinc-600 whitespace-nowrap">
                              {formatTimeAgo(notif.time)}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-400 line-clamp-2">
                            {notif.text}
                          </p>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-white/10 text-center">
              <a
                href="/crm/notifications"
                className="text-sm text-purple-400 hover:text-purple-300 font-semibold inline-flex items-center gap-1"
                onClick={() => setIsOpen(false)}
              >
                View all notifications
                <ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
